import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './config/queryClient';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ToastProvider } from './contexts/ToastContext';
import Layout from './components/Layout';
import FullWidthLayout from './components/FullWidthLayout';
import CoachmarkProvider from './components/chrome/CoachmarkProvider';
import { PageSkeleton } from './components/chrome/Skeleton';
import GeogusserFlowStatus from './components/geogusser/GeogusserFlowStatus';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import AdminLayout from './components/admin/AdminLayout';
import './styles/components.css';
import { installMetricsCapture, trackVisitedUrl } from './services/feedbackMetrics';

const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const MapListPage = lazy(() => import('./pages/MapListPage'));
const StrategyListPage = lazy(() => import('./pages/StrategyListPage'));
const StrategyDetailPage = lazy(() => import('./pages/StrategyDetailPage'));
const MapUploadPage = lazy(() => import('./pages/MapUploadPage'));
const GeogusserHomePage = lazy(() => import('./pages/GeogusserHomePage'));
const GeogusserLobbyPage = lazy(() => import('./pages/GeogusserLobbyPage'));
const GeogusserGamePage = lazy(() => import('./pages/GeogusserGamePage'));
const GeogusserResultsPage = lazy(() => import('./pages/GeogusserResultsPage'));
const GeogusserLeaderboardsPage = lazy(() => import('./pages/GeogusserLeaderboardsPage'));
const TournamentToolsPage = lazy(() => import('./pages/TournamentToolsPage'));
const TournamentInvitePage = lazy(() => import('./pages/TournamentInvitePage'));
const TournamentDetailPage = lazy(() => import('./pages/TournamentDetailPage'));
const TournamentCreatePage = lazy(() => import('./pages/TournamentCreatePage'));
const TournamentBracketPage = lazy(() => import('./pages/TournamentBracketPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminBadgesPage = lazy(() => import('./pages/admin/AdminBadgesPage'));
const AdminGamesPage = lazy(() => import('./pages/admin/AdminGamesPage'));
const AdminMapsPage = lazy(() => import('./pages/admin/AdminMapsPage'));
const AdminGeogusserMapsPage = lazy(() => import('./pages/admin/AdminGeogusserMapsPage'));
const AdminGeoguesserOverviewPage = lazy(() => import('./pages/admin/AdminGeoguesserOverviewPage'));
const AdminCacheStatsPage = lazy(() => import('./pages/admin/AdminCacheStatsPage'));
const AdminLevelsPage = lazy(() => import('./pages/admin/AdminLevelsPage'));
const AdminLevelSubmissionsPage = lazy(() => import('./pages/admin/AdminLevelSubmissionsPage'));
const AdminDailyChallengePage = lazy(() => import('./pages/admin/AdminDailyChallengePage'));
const AdminGameModesPage = lazy(() => import('./pages/admin/AdminGameModesPage'));
const AdminSessionsPage = lazy(() => import('./pages/admin/AdminSessionsPage'));
const AdminActivityPage = lazy(() => import('./pages/admin/AdminActivityPage'));
const AdminDeprecationsPage = lazy(() => import('./pages/admin/AdminDeprecationsPage'));
const AdminGeoDataPage = lazy(() => import('./pages/admin/AdminGeoDataPage'));
const AdminGeoFloorsPage = lazy(() => import('./pages/admin/AdminGeoFloorsPage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminStrategiesPage = lazy(() => import('./pages/admin/AdminStrategiesPage'));
const AdminFeedbackListPage = lazy(() => import('./pages/admin/AdminFeedbackListPage'));
const AdminFeedbackDetailPage = lazy(() => import('./pages/admin/AdminFeedbackDetailPage'));
const TeamsListPage = lazy(() => import('./pages/TeamsListPage'));
const TeamDetailPage = lazy(() => import('./pages/TeamDetailPage'));
const TeamFormPage = lazy(() => import('./pages/TeamFormPage'));
const MyTeamsPage = lazy(() => import('./pages/MyTeamsPage'));
const NotificationPage = lazy(() => import('./pages/NotificationPage'));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const BadgesPage = lazy(() => import('./pages/BadgesPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));

const App = () => {
  React.useEffect(() => {
    installMetricsCapture();
    // Track initial URL
    trackVisitedUrl(window.location.pathname + window.location.search);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <NotificationProvider>
            <ToastProvider>
            <CoachmarkProvider>
            <Router>
          <RouteTracker />
          <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route path="/" element={<Layout><HomePage /></Layout>} />
            <Route path="/changelog" element={<Layout><ChangelogPage /></Layout>} />
            <Route path="/about" element={<Layout><AboutPage /></Layout>} />
            <Route path="/badges" element={<Layout><BadgesPage /></Layout>} />
            <Route path="/privacy" element={<Layout><PrivacyPolicyPage /></Layout>} />
            <Route path="/terms" element={<Layout><TermsOfServicePage /></Layout>} />
            <Route path="/login" element={<Layout><LoginPage /></Layout>} />
            <Route path="/signup" element={<Layout><SignupPage /></Layout>} />
            <Route path="/verify-email" element={<Layout><VerifyEmailPage /></Layout>} />
            <Route path="/u/:username" element={<Layout><UserProfilePage /></Layout>} />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Layout>
                    <SettingsPage />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/maps"
              element={
                <Layout>
                  <MapListPage />
                </Layout>
              }
            />
            <Route
              path="/maps/:mapId/strategies"
              element={
                <Layout>
                  <StrategyListPage />
                </Layout>
              }
            />
            <Route
              path="/maps/:mapId/strategies/:strategyId"
              element={
                <FullWidthLayout useBodyScroll>
                  <StrategyDetailPage />
                </FullWidthLayout>
              }
            />
            <Route
              path="/maps/upload"
              element={
                <PrivateRoute>
                  <Layout>
                    <MapUploadPage />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/geoguesser"
              element={
                <Layout>
                  <GeogusserHomePage />
                </Layout>
              }
            />
            <Route
              path="/geoguesser/leaderboards"
              element={
                <Layout>
                  <GeogusserLeaderboardsPage />
                </Layout>
              }
            />
            <Route
              path="/geoguesser/game/:sessionId"
              element={
                <Suspense
                  fallback={
                    <GeogusserFlowStatus
                      variant="immersive"
                      message="Loading game"
                    />
                  }
                >
                  <Layout>
                    <GeogusserGamePage />
                  </Layout>
                </Suspense>
              }
            />
            <Route
              path="/geoguesser/lobby/:sessionId"
              element={
                <Layout>
                  <Suspense fallback={<GeogusserFlowStatus message="Loading lobby" />}>
                    <GeogusserLobbyPage />
                  </Suspense>
                </Layout>
              }
            />
            <Route
              path="/tournament-tools"
              element={
                <Layout>
                  <TournamentToolsPage />
                </Layout>
              }
            />
            <Route
              path="/tournaments"
              element={
                <Layout>
                  <TournamentToolsPage />
                </Layout>
              }
            />
            <Route
              path="/tournaments/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <TournamentCreatePage />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/tournaments/:tournamentId"
              element={
                <Layout>
                  <TournamentDetailPage />
                </Layout>
              }
            />
            <Route
              path="/tournaments/:tournamentId/bracket"
              element={
                <FullWidthLayout>
                  <TournamentBracketPage />
                </FullWidthLayout>
              }
            />
            <Route
              path="/tournaments/:tournamentId/standings"
              element={<Navigate to=".." replace />}
            />
            <Route
              path="/tournaments/invite"
              element={
                <Layout>
                  <TournamentInvitePage />
                </Layout>
              }
            />
            <Route
              path="/t/invite/:code"
              element={
                <Layout>
                  <TournamentInvitePage />
                </Layout>
              }
            />
            <Route
              path="/geoguesser/results/:sessionId"
              element={
                <Layout>
                  <GeogusserResultsPage />
                </Layout>
              }
            />

            {/* Teams Routes */}
            <Route
              path="/teams"
              element={
                <Layout>
                  <TeamsListPage />
                </Layout>
              }
            />
            <Route
              path="/teams/my-teams"
              element={
                <PrivateRoute>
                  <Layout>
                    <MyTeamsPage />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/teams/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <TeamFormPage />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/teams/:teamId"
              element={
                <Layout>
                  <TeamDetailPage />
                </Layout>
              }
            />
            <Route
              path="/teams/:teamId/edit"
              element={
                <PrivateRoute>
                  <Layout>
                    <TeamFormPage />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Notifications Route */}
            <Route
              path="/notifications"
              element={
                <PrivateRoute>
                  <Layout>
                    <NotificationPage />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/badges"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminBadgesPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/games"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminGamesPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/maps"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminMapsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/strategies"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminStrategiesPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/geoguesser"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminGeoguesserOverviewPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/geoguesser-maps"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminGeogusserMapsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/game-modes"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminGameModesPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/daily-challenge"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminDailyChallengePage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/levels"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminLevelsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/level-submissions"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminLevelSubmissionsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            {/* Remove duplicate game-modes placeholder route */}
            <Route
              path="/admin/sessions"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminSessionsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/activity"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminActivityPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/feedback"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminFeedbackListPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/feedback/:id"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminFeedbackDetailPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/cache-stats"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminCacheStatsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/deprecations"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminDeprecationsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/geoguesser-floors"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminGeoFloorsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/geoguesser-data"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminGeoDataPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
          </Routes>
          </Suspense>
        </Router>
            </CoachmarkProvider>
            </ToastProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
    {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

export default App;

// Track route changes for visited URLs
function RouteTracker() {
  const location = useLocation();
  React.useEffect(() => {
    trackVisitedUrl(location.pathname + location.search);
  }, [location]);
  return null;
}
